import { SortableHeader } from '@/components/search/SortableHeader'
import React, { useEffect, useMemo, useState } from 'react'
import { useModal } from '@/components/modal/Modal'
import { StaticPaginatedTable } from '@/components/search/StaticPaginatedTable'
import { Spinner } from '@/components/spinner'
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { BackButton } from '@/components/layout/NavBar'
import { useAppContext } from '@/context/app'
import { Menu, MenuPopupBody } from '@/popups/menupopup/menu'

import { LocationEditPopup, LocationPathLabel, LocationRemovePopup, LocationRow, ParentLocationSelectPopup, ROOT_LOCATION_ID } from './common'
import { SearchInput } from '@/components/search/SearchInput'

const LocationMenuPopup = ({
  onClose,
  row,
  onReload,
}: {
  onClose: () => void
  row: LocationRow
  onReload: () => void
}) => {
  const { openModal } = useModal()

  const menu: Menu = {
    title: row.name,
    rows: [
      {
        icon: 'svgfont-edit',
        title: 'elements.table.locations.menu.edit.edit_row',
        action: () => openModal((props) => <LocationEditPopup {...props} locationId={row.id} onSaved={onReload} />),
      },
      {
        icon: 'svgfont-remove',
        title: 'elements.table.locations.menu.edit.remove',
        action: () => openModal((props) => <LocationRemovePopup {...props} location={row} onRemoved={onReload} />),
      },
    ],
  }

  return <MenuPopupBody menu={menu} onClose={onClose} />
}

export const CommunalRemanentLocationsManagerPage = () => {
  const { services, session, translates } = useAppContext()
  const { openModal } = useModal()
  const [locations, setLocations] = useState<LocationRow[] | null>(null)
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [filtred, setFiltred] = useState(locations)

  const subLocations = useMemo(() => {
    if (!locations) {
      return []
    }
    return locations
      .filter((location) => (location.parentId ?? null) === selectedParentId)
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [locations, selectedParentId])

  useEffect(() => {
    if (!debouncedSearch || debouncedSearch === '') {
      setFiltred(subLocations)
      return
    }
    setFiltred(subLocations?.filter((v) => {
      return v.name.indexOf(debouncedSearch) >= 0
    }) || null)
  }, [debouncedSearch, subLocations])

  const loadLocations = async () => {
    const response = await services.session.fetchWithToken('/api/communal/remanent/locations')
    if (response.status !== 200) {
      setLocations([])
      return
    }
    const data = await response.json()
    setLocations(Array.isArray(data) ? data : [])
  }

  useEffect(() => {
    loadLocations()
  }, [])

  const selectedParentLocation = useMemo(() => {
    if (selectedParentId === null || !locations) {
      return null
    }
    return locations.find((location) => location.id === selectedParentId) || null
  }, [selectedParentId, locations])


  if (!session || !translates || !locations) {
    return <Spinner />
  }

  return <LoggedInPageLayout
    pageName='locations-manager'
    services={services}
    session={session}
    navbarLeft={<BackButton services={services} translates={session.translates} />}
  >

    <LocationPathLabel locations={locations} selectedId={selectedParentId} onClick={(row) => {
      if (selectedParentLocation?.id === row?.id) {
        return openModal((props) => <ParentLocationSelectPopup
          {...props}
          locations={locations}
          selectedId={row?.id}
          onApprove={(value) => setSelectedParentId(value)}
        />)
      }
      setSelectedParentId(row ? row.id : ROOT_LOCATION_ID)
    }} />

    <StaticPaginatedTable<LocationRow>
      sortValues={{
        name: row => row.name
      }}
      data={filtred || []}
      translates={translates}
      toolboxLeft={() => <>
        <div
          className='tool'
          onClick={() => openModal((props) => <LocationEditPopup {...props} defaultParentId={selectedParentId} onSaved={loadLocations} />)}
        >
          <i className='svgfont-add' />
        </div>
      </>}
      toolboxRight={() => {
        return <>
          <SearchInput translates={translates} value={search} onChange={setSearch} onDebouncedChange={setDebouncedSearch} />
        </>
      }}
      header={(query, onSort) => <>
        <SortableHeader field='name' query={query} onSort={onSort}>{translates.for('elements.table.locations.name')}</SortableHeader>
        {/* <div className='hcol'>{translates.for('elements.table.locations.coordinates')}</div> */}
        {/* <div className='hcol'>Parent</div> */}
        <div className='hcol'></div>
      </>}
      row={(row) => <>
        <div className='col' onClick={() => setSelectedParentId(row.id)}>{row.name}</div>
        {/* <div className='col'>
          {row.latitude != null && row.longitude != null
            ? `${row.latitude}, ${row.longitude}`
            : translates.for('elements.table.locations.empty_coordinates')}
        </div> */}
        <div className='col icocol' onClick={() => openModal((props) => <LocationMenuPopup {...props} row={row} onReload={loadLocations} />)}>
          <i className='svgfont-menu' />
        </div>
      </>}
    />
  </LoggedInPageLayout>
}
